import * as React from 'react';
import { styled } from 'styled-components';
import kellyPartnersLogoBlueSvg from '../../images/kellyPartnersLogoBlue.svg';
import AutoLogoutLayout from './common/Layout';
import Helmet from '../../commonComponents/Helmet';
import { tabletPortraitMinWidth } from '../cssConstants';
import config from '../../config.general';
import { betterOffAppName, kellyPartnersPassportAppName, mcProgressAppName } from '../../core/constants';
import { getAppDisplayName } from '../../core/appType';

interface StoreLinks {
	ios?: string;
	android?: string;
}

const storeLinksByAppName: Record<string, StoreLinks> = {
	[kellyPartnersPassportAppName]: {
		ios: 'https://apps.apple.com/app/kelly-partners-passport/id6456267642'
	},
	[mcProgressAppName]: {
		ios: 'https://apps.apple.com/app/progress-ai/id6738490263'
	},
	[betterOffAppName]: {
		ios: 'https://apps.apple.com/app/be-better-off/id6759538641',
		android: 'https://play.google.com/store/apps/details?id=com.kellypartners.betteroff'
	}
};

type Platform = 'ios' | 'android' | 'desktop';

const detectPlatform = (): Platform => {
	if (typeof window === 'undefined') return 'desktop';
	const ua = window.navigator.userAgent;
	if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
	if (/Android/i.test(ua)) return 'android';
	return 'desktop';
};

const ProtectedFallbackPage = () => {
	const platform = detectPlatform();
	const links = storeLinksByAppName[config.appName] ?? {};
	const showIos = (platform === 'ios' || platform === 'desktop') && links.ios;
	const showAndroid = (platform === 'android' || platform === 'desktop') && links.android;

	return (
		<AutoLogoutLayout onBackButtonClick={null} showHeader={false} showFooter={false}>
			<Helmet>
				<title>Continue with Mobile App</title>
			</Helmet>
			<HeaderWrapper>
				<HeaderLogo src={kellyPartnersLogoBlueSvg} alt="Kelly+Partners Logo" />
			</HeaderWrapper>
			<Content>
				<Heading>Open in the {getAppDisplayName()} app</Heading>
				<Paragraph>To continue, please open this link in the latest version of the mobile app.</Paragraph>
				<Paragraph>If you don&apos;t have the latest version installed, get it from the store below.</Paragraph>
				{(showIos || showAndroid) && (
					<ButtonRow>
						{showIos && (
							<StoreButton href={links.ios} target="_blank" rel="noreferrer">
								Get it on the App Store
							</StoreButton>
						)}
						{showAndroid && (
							<StoreButton href={links.android} target="_blank" rel="noreferrer">
								Get it on Google Play
							</StoreButton>
						)}
					</ButtonRow>
				)}
			</Content>
		</AutoLogoutLayout>
	);
};

export default ProtectedFallbackPage;

const HeaderWrapper = styled.section`
	text-align: center;
	padding-top: 40px;
`;

const HeaderLogo = styled.img`
	height: auto;
	width: 227px;

	@media (min-width: ${tabletPortraitMinWidth}) {
		width: 350px;
	}
`;

const Content = styled.section`
	text-align: center;
	max-width: 480px;
	margin: 60px auto;
	padding: 0 24px;
`;

const Heading = styled.h1`
	font-size: 24px;
	line-height: 30px;
	font-weight: 600;
	margin-bottom: 24px;
`;

const Paragraph = styled.p`
	font-size: 16px;
	line-height: 22px;
	margin: 12px 0;
`;

const ButtonRow = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	margin-top: 32px;

	@media (min-width: ${tabletPortraitMinWidth}) {
		flex-direction: row;
		justify-content: center;
	}
`;

const StoreButton = styled.a`
	display: inline-block;
	background: #005e9e;
	color: #ffffff;
	text-decoration: none;
	padding: 14px 24px;
	border-radius: 8px;
	font-size: 16px;
	font-weight: 600;

	&:hover {
		background: #004a7d;
	}
`;
